/**
 * Phase 140B.1 — Broadcaster Audio Enhancement Panel.
 *
 * Three WebRTC constraint toggles that live alongside the score panel +
 * health badge in the broadcaster cockpit. Toggling persists immediately
 * to localStorage (via the `useAudioEnhancementSettings` hook in the
 * parent). The audio constraints are applied at LiveKitRoom connect time
 * via the `audio` prop — so toggles flipped MID-broadcast require the
 * broadcaster to restart the stream to take effect. We surface that with
 * an honest hint pill below the toggles when `liveBroadcastActive` is
 * true AND the current settings differ from the settings captured at
 * broadcast start.
 *
 * Defensive: pure visual + onChange; no LiveKit, no API calls. The parent
 * owns the source of truth.
 */
import { useState } from 'react';
import { Mic, ShieldCheck, Activity, Volume2, AlertCircle } from 'lucide-react';

function Toggle({ label, hint, on, onChange, testid }) {
  return (
    <button
      type="button"
      onClick={() => onChange(!on)}
      data-testid={testid}
      aria-pressed={on}
      className="w-full flex items-center justify-between gap-3 p-3 rounded-lg bg-[#0c1a2e] border-2 border-[#22314F] hover:border-[#FF5A1F] transition-all text-left"
    >
      <div className="flex-1 min-w-0">
        <div className="text-sm font-bold text-white">{label}</div>
        <div className="livex-body-text" style={{ fontSize: '12px', marginTop: '2px' }}>{hint}</div>
      </div>
      <div
        className={`shrink-0 h-6 w-11 rounded-full transition-all relative ${on ? 'bg-emerald-500' : 'bg-white/15'}`}
        aria-hidden
      >
        <div
          className={`absolute top-0.5 h-5 w-5 rounded-full bg-white shadow transition-all ${on ? 'left-[22px]' : 'left-0.5'}`}
        />
      </div>
    </button>
  );
}

export default function BroadcasterAudioPanel({
  settings,
  setSetting,
  liveBroadcastActive = false,
}) {
  // Snapshot the settings the broadcaster connected with. Captured via
  // lazy useState initialiser — runs exactly once when the component
  // mounts. LivexStudio is expected to pass a `key={stream.id}` so each
  // new broadcast remounts the panel and re-snapshots fresh.
  const [snapshot] = useState(() => (liveBroadcastActive ? { ...settings } : null));

  // Derive `pendingRestart` purely from current settings vs snapshot.
  const pendingRestart = !!(liveBroadcastActive && snapshot && (
    snapshot.noiseSuppression !== settings.noiseSuppression
    || snapshot.echoCancellation !== settings.echoCancellation
    || snapshot.autoGainControl !== settings.autoGainControl
  ));

  return (
    <div
      className="p-5 bg-[#07111F] border-2 border-[#22314F] rounded-xl space-y-3"
      data-testid="broadcaster-audio-panel"
    >
      <div className="flex items-center gap-2">
        <Volume2 className="h-4 w-4 text-[#FF5A1F]" />
        <div className="livex-label" style={{ marginBottom: 0 }}>Audio Enhancement</div>
      </div>

      <Toggle
        label="Noise Suppression"
        hint="Cuts wind, crowd, and traffic noise."
        on={settings.noiseSuppression}
        onChange={(v) => setSetting('noiseSuppression', v)}
        testid="audio-toggle-noise-suppression"
      />
      <Toggle
        label="Echo Cancellation"
        hint="Removes echo from indoor venues and phone speakers."
        on={settings.echoCancellation}
        onChange={(v) => setSetting('echoCancellation', v)}
        testid="audio-toggle-echo-cancellation"
      />
      <Toggle
        label="Auto Gain Control"
        hint="Boosts quiet voices and tames sudden loud sounds."
        on={settings.autoGainControl}
        onChange={(v) => setSetting('autoGainControl', v)}
        testid="audio-toggle-auto-gain"
      />

      {pendingRestart && (
        <div
          className="flex items-start gap-2 p-3 rounded-lg bg-amber-950/40 border-2 border-amber-700/60"
          data-testid="audio-toggle-restart-hint"
        >
          <AlertCircle className="h-4 w-4 text-amber-400 mt-0.5 flex-shrink-0" />
          <div className="text-xs text-amber-100 font-bold leading-snug">
            Restart this broadcast to apply the new audio settings.
            <div className="livex-body-text" style={{ fontSize: '11px', marginTop: '4px', color: '#fde68a' }}>
              Changes will take effect on your next stream.
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
